/**
 * Public marketing feeds — NO AUTH. The landing's "Now Flowing" ephemeris and the
 * playable template gallery read the REAL registries through two anonymous,
 * sanitized endpoints (products/diwa/src/diwa/public_site.py):
 *
 *   GET /api/diwa/public/springs    -> { springs: [{name, display, carries, cadence,
 *                                        health, last_drop_minutes, last_drop_rows}] }
 *   GET /api/diwa/public/templates  -> { templates: [{key, name, description, shape,
 *                                        tags, entity_types, words, ego_relations,
 *                                        springs, has_default_rules}] }
 *
 * Both degrade to a clearly-labelled STATIC SNAPSHOT (the caller renders the
 * `live` flag honestly) so the page never breaks — but never quietly pretends a
 * snapshot is live data.
 */

const base = '/api/diwa'

export interface PublicSpring {
  name: string
  display: string
  /** What flows out of it — the catalog gloss. */
  carries: string
  /** Plain cadence words: "hourly" / "daily" / "every 6h" / "on demand" / "scheduled". */
  cadence: string
  /** Coarse health: flowing / late / throttled / failing / resting. */
  health: string
  last_drop_minutes: number | null
  last_drop_rows: number | null
}

export interface PublicTemplateSpring {
  name: string
  display: string
  gloss: string
}

export interface PublicTemplate {
  key: string
  name: string
  description: string
  shape: string
  tags: string[]
  entity_types: string[]
  relation_types: string[]
  /** Every entity type in plain words — the peek's vocabulary. */
  words: Record<string, string>
  ego_relations: Array<{ name: string; kind: string; word: string }>
  springs: PublicTemplateSpring[]
  has_default_rules: boolean
}

export async function fetchPublicSprings(): Promise<PublicSpring[]> {
  const r = await fetch(`${base}/public/springs`)
  if (!r.ok) throw new Error(`public springs: ${r.status}`)
  const body = (await r.json()) as { springs?: PublicSpring[] }
  return body.springs ?? []
}

export async function fetchPublicTemplates(): Promise<PublicTemplate[]> {
  const r = await fetch(`${base}/public/templates`)
  if (!r.ok) throw new Error(`public templates: ${r.status}`)
  const body = (await r.json()) as { templates?: PublicTemplate[] }
  return body.templates ?? []
}

// ── Static snapshots (the honest fallback — labelled as such where rendered) ──

export const SPRINGS_SNAPSHOT: PublicSpring[] = [
  { name: 'sam-opportunities', display: 'SAM.gov opportunities', carries: 'Open federal solicitations as they post — the work you could bid on.', cadence: 'every 6h', health: 'flowing', last_drop_minutes: null, last_drop_rows: null },
  { name: 'usaspending-awards', display: 'USAspending awards', carries: 'Contracts the government actually signed — who won what, for how much.', cadence: 'daily', health: 'flowing', last_drop_minutes: null, last_drop_rows: null },
  { name: 'anilist-airing', display: 'AniList airing seasons', carries: "The season's airing schedule, episode by episode, straight from AniList.", cadence: 'every 6h', health: 'flowing', last_drop_minutes: null, last_drop_rows: null },
  { name: 'sec-press', display: 'SEC press releases', carries: 'What the regulator itself announces — enforcement, rulemaking, market news.', cadence: 'hourly', health: 'flowing', last_drop_minutes: null, last_drop_rows: null },
  { name: 'fed-press', display: 'Federal Reserve press', carries: 'The Fed in its own words — rate decisions, statements, speeches.', cadence: 'hourly', health: 'flowing', last_drop_minutes: null, last_drop_rows: null },
]

export const TEMPLATES_SNAPSHOT: PublicTemplate[] = [
  {
    key: 'govcon', name: 'GovCon Pipeline',
    description: 'Federal opportunities, agencies, awards and vendors — a pipeline toward bids.',
    shape: 'pipeline', tags: ['govcon', 'work'],
    entity_types: ['agency', 'opportunity', 'vendor'], relation_types: ['bidding_on', 'holds', 'issued_by'],
    words: { agency: 'who is buying', opportunity: 'work you could bid on', vendor: 'who else is in the market' },
    ego_relations: [{ name: 'bidding_on', kind: 'event', word: 'you are bidding on it' }],
    springs: [
      { name: 'sam-opportunities', display: 'SAM.gov opportunities', gloss: 'Open federal solicitations as they post.' },
      { name: 'usaspending-awards', display: 'USAspending awards', gloss: 'Contracts the government actually signed.' },
    ],
    has_default_rules: true,
  },
  {
    key: 'markets', name: 'Markets Watch',
    description: 'Companies, tickers and catalysts you follow — an outside world, monitored.',
    shape: 'watch', tags: ['finance', 'markets'],
    entity_types: ['company', 'signal', 'theme'], relation_types: ['mentions', 'part_of'],
    words: { company: 'a firm you watch', signal: 'something that just changed', theme: 'a thesis it belongs to' },
    ego_relations: [{ name: 'watching', kind: 'state', word: 'you are watching it' }],
    springs: [
      { name: 'sec-press', display: 'SEC press releases', gloss: 'What the regulator itself announces.' },
      { name: 'fed-press', display: 'Federal Reserve press', gloss: 'The Fed in its own words.' },
    ],
    has_default_rules: false,
  },
  {
    key: 'media-library', name: 'Media Library',
    description: 'A living shelf — series, creators and studios, with the airing season flowing in.',
    shape: 'library', tags: ['media'],
    entity_types: ['creator', 'studio', 'title'], relation_types: ['created_by', 'produced_by'],
    words: { creator: 'the person who made it', studio: 'who animated it', title: 'an anime or manga you follow' },
    ego_relations: [{ name: 'watched', kind: 'event', word: 'you watched it' }],
    springs: [{ name: 'anilist-airing', display: 'AniList airing seasons', gloss: "The season's schedule, episode by episode." }],
    has_default_rules: false,
  },
]
